<template>
    <div>
        <div v-if="!loan && wait" class="p-4 bg-white mb-3 shadow">
            <h4 class="font-weight-bold">View Loan</h4>
            <b class="text-success"><img src="../assets/loader.gif">Loading loan details, Please wait...</b>
        </div>

        <div class="row" v-if="loan">
            <div class="col-md-7">
                <div class="p-3 bg-white shadow-sm mb-2 rounded-lg">
                    <alerts :_success="success" :_error="error"></alerts>
                    <h4 class="font-weight-bold">Loan Details</h4>

                    <table class="table table-borderless table-sm">
                        <tr>
                            <th style="width: 40%">Loan ID</th>
                            <td>{{ loan.serial }}</td>
                        </tr>
                        <tr>
                            <th>Loan Type</th>
                            <td>{{ loan.product.name }}</td>
                        </tr>
                        <tr>
                            <th>Loan Date</th>
                            <td>{{ day(loan.created_at).format('MMM, DD YYYY') }}</td>
                        </tr>
                        <tr>
                            <th>Customer</th>
                            <td>{{ loan.user.name }}</td>
                        </tr>
                        <tr>
                            <th>Status</th>
                            <td><span :class="`status ${loan.status}`">{{ loan.status }}</span></td>
                        </tr>
                        <tr>
                            <th>Applied Amount</th>
                            <td>{{ loan.amount }}</td>
                        </tr>
                        <tr>
                            <th>Interest</th>
                            <td>{{ loan.rate }}% <span class="text-muted">({{ loan.interest }})</span></td>
                        </tr>
                        <tr>
                            <th>Repayment</th>
                            <td>{{ loan.repayment }}</td>
                        </tr>
                        <tr>
                            <th>Total Paid</th>
                            <td>{{ loan.paid }}</td>
                        </tr>
                        <tr>
                            <th>Current Balance</th>
                            <td>{{ loan.balance }}</td>
                        </tr>
                        <tr>
                            <th>Loan Purpose</th>
                            <td>{{ loan.purpose }}</td>
                        </tr>
                    </table>

                    <br>
                    <div class="p-3 bg-white border rounded mb-3">
                        <h5 class="font-weight-bold text-info">Loan Disbursement</h5>
                        <div v-if="loan.isDisbursed">
                            <p class="text-muted">Loan is disbursed</p>
                            <b>Disbursed By</b>: USD {{ loan.approval.disbursed_by.name }} <br>
                            <b>Disbursement Date:</b> {{ day(loan.approval.disbursed_at).format('MMM, DD YYYY H:mm') }}
                        </div>
                        <div v-else>
                            Loan is {{ loan.status }}
                        </div>
                    </div>
                </div>

                <!-- Loan Schedule -->
                <div class="p-3 bg-white shadow-sm mb-2 rounded-lg">
                    <h5 class="font-weight-bold">Payment Schedule</h5>
                    <table class="table table-sm table-borderless">
                        <thead class="bg-info text-white">
                        <tr>
                            <th>DATE</th>
                            <th>STATUS</th>
                            <th class="text-right">DUE</th>
                            <th class="text-right">PAID</th>
                            <th class="text-right">BALANCE</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-if="loan.orders.length == 0">
                            <td colspan="5" class="text-muted">Payment schedule will appear once loan is disbursed</td>
                        </tr>
                        <tr v-for="i in loan.orders" :key="i.id">
                            <td>{{ day(i.created_at).format('MMM, DD YYYY') }}</td>
                            <td><span :class="'status '+ i.status">{{ i.status }}</span></td>
                            <td class="text-right">{{ i.amount }}</td>
                            <td class="text-right">{{ i.paid }}</td>
                            <td class="text-right">{{ i.balance }}</td>
                        </tr>
                        </tbody>
                        <tfoot>
                        <tr>
                            <th colspan="2">TOTALS</th>
                            <th class="text-right">{{ loan.amount }}</th>
                            <th class="text-right">{{ loan.paid }}</th>
                            <th class="text-right">{{ loan.balance }}</th>
                        </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <div class="col-md-5">
                <!-- Loan Approval -->
                <div class="p-3 bg-white shadow-sm rounded-lg mb-3">
                    <h5 class="font-weight-bold">Loan Approval</h5>
                    <div v-if="loan.isApproved">
                        <p class="text-muted">Loan forwarded for approval</p>
                        <b>Approval Date:</b> {{ day(loan.approval.approved_at).format('MMM, DD YYYY H:mm') }}<br>
                        <b>Approved Amount</b>: USD {{ loan.amount }} <br>
                        <b>Approved By:</b> {{ loan.approval.approved_by.name }}<br>
                        <b>Approval Remarks:</b> {{ loan.approval.approval_remarks }}<br>
                    </div>
                    <div v-else>
                        Loan is {{ loan.status }}
                    </div>
                </div>

                <!-- Loan Verification -->
                <div class="p-3 bg-white shadow-sm rounded-lg mb-3">
                    <h5 class="font-weight-bold">Loan Verification</h5>
                    <div v-if="loan.isVerified">
                        <p class="text-muted">Loan verification</p>
                        <b>Verified By:</b> {{ loan.approval.verified_by.name }}<br>
                        <b>Date Verified:</b> {{ day(loan.approval.verified_at).format('MMM, DD YYYY H:mm') }}
                    </div>
                    <div v-else>
                        Loan is {{ loan.status }}
                    </div>
                </div>

                <!-- Loan Rejection -->
                <div v-if="loan.isDeclined" class="p-4 bg-white shadow-sm rounded mb-2">
                    <h5 class="font-weight-bold text-danger">Decline Loan</h5>
                    <p class="text-muted">Decline Loan Application</p>
                    <b>Date Declined:</b> {{ day(loan.approval.declined_at).format('MMM, DD YYYY H:mm') }}<br>
                    <b>Declined by:</b> {{ loan.approval.declined_by.name }}<br>
                    <b>Reason for declining:</b> {{ loan.approval.decline_remarks }}<br>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Alerts from "@/components/Alerts";

export default {
    name: "ViewLoan",
    components: {Alerts},
    props: ['id'],
    data() {
        return {
            loan: null,
        }
    },
    mounted() {
        this.getLoan()
    },
    methods: {
        getLoan() {
            this.setLoading()
            this.clear();
            this.success = "Getting loan details, please wait..."
            this.$http.get(`/user/loans/${this.id}`).then(response => {
                this.loan = response.data.data
                this.success = ''
            }).catch(error => {
                this.showError(error)
            }).then(() => {
                this.wait = false
            })
        }
    }
}
</script>

<style scoped>

</style>